import React from 'react';
import { useParams } from 'react-router-dom';
import { EditorService } from '../../../services/editor';
import { InfrastructureResourceType } from '@elyx-code/definitions';
import { resolveActionDescriptorNameById, toSentenceCase, toKebabCase } from '@elyx-code/project-logic-tree';
import SharedDriveIcon from '../../../assets/shared-drive.svg';

interface ISharedDriveViewerProps {
	onClose: () => void;
	project: EditorService;
	projectId: string;
}

const SharedDriveViewer: React.FC<ISharedDriveViewerProps> = ({ onClose, project, projectId }) => {
	const params = useParams<{ resourceId: string }>();
	const resourceId = params.resourceId || '';

	const headerInfo = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];
		const matchingRes = resources.find(r => r.resourceId === resourceId && r.resourceType === (InfrastructureResourceType as any).SharedDrive);
		
		const matchEntity = (project?.lastPublishedProjectState?.entities || []).find((e: any) => e.id && resourceId.includes(toKebabCase(e.id)));
		const resolvedName = matchEntity ? (matchEntity.name || resolveActionDescriptorNameById(matchEntity.id)) : null;

		if (matchingRes || matchEntity) {
			const rawName = matchingRes?.name || resourceId;
			const readableName = resolvedName || resolveActionDescriptorNameById(rawName) || toSentenceCase(rawName) || 'Shared Drive';
			return {
				name: readableName,
				icon: 'fa-solid fa-hard-drive',
				details: matchingRes?.details || {},
			};
		}
		
		return {
			name: resourceId || 'Shared Drive',
			icon: 'fa-solid fa-hard-drive',
			details: {},
		};
	}, [project?.liveResources, resourceId]);

	const handleBack = () => {
		window.history.back();
	};

	const formatBytes = (bytes: number) => {
		if (bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	return (
		<div
			style={{
				position: 'relative',
				height: 'calc(100% - 90px)',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					padding: '24px 40px',
					fontFamily: "'Outfit', 'Inter', sans-serif",
				}}
			>
				{/* Top Panel */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: '20px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
							<button
								onClick={handleBack}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									fontSize: '12px',
									fontWeight: 500,
									color: '#6366f1',
									padding: 0,
									alignSelf: 'flex-start',
									transition: 'color 0.15s ease',
								}}
								onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
								onMouseLeave={(e) => (e.currentTarget.style.color = '#6366f1')}
							>
								<i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }}></i>
								Back to resources
							</button>

							<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
								<img src={SharedDriveIcon} alt="" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
								<h2 style={{ margin: '0px', fontSize: '18px', fontWeight: 600, color: '#111827' }}>
									{headerInfo.name}
								</h2>
							</div>
							
							<span style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
								Shared Drive Details
							</span>
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '20px',
						maxWidth: '600px',
					}}
				>
					<div
						style={{
							padding: '24px',
							border: '1px solid #e0e0e0',
							borderRadius: '10px',
							backgroundColor: '#fdfdfd',
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '12px' }}>
							<span style={{ fontWeight: 600, color: '#444', fontSize: '14px' }}>File System ID</span>
							<span style={{ fontFamily: 'monospace', color: '#666', fontSize: '13px' }}>{headerInfo.details.fileSystemId || 'N/A'}</span>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '12px' }}>
							<span style={{ fontWeight: 600, color: '#444', fontSize: '14px' }}>Status</span>
							<span style={{ color: headerInfo.details.lifeCycleState === 'available' ? '#22c55e' : '#f59e0b', fontSize: '13px', fontWeight: 500, textTransform: 'capitalize' }}>
								{headerInfo.details.lifeCycleState || 'Unknown'}
							</span>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '12px' }}>
							<span style={{ fontWeight: 600, color: '#444', fontSize: '14px' }}>Size</span>
							<span style={{ color: '#666', fontSize: '13px' }}>
								{headerInfo.details.sizeInBytes !== undefined ? formatBytes(headerInfo.details.sizeInBytes) : 'Unknown'}
							</span>
						</div>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							<span style={{ fontWeight: 600, color: '#444', fontSize: '14px' }}>Encrypted</span>
							<span style={{ color: '#666', fontSize: '13px' }}>
								{headerInfo.details.encrypted ? 'Yes' : 'No'}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SharedDriveViewer;
